import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Mail, UserRoundPlus, UsersRound, X } from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { Avatar, Button, Field, Input, Modal, cx } from "../ui"

/**
 * Invite people to one issue.
 *
 * Two routes in, because the person doing it knows one of two things: who they
 * are, or their email address. Access is granted per issue and nothing else -
 * being added here does not open the rest of the project.
 *
 * Teams are deliberately not here. A team belongs to the project, invited once
 * by its owner, and stays on every issue raised afterwards; offering it again
 * per issue would suggest the two work the same way, and they do not.
 */
export function InviteDialog({ open, onClose, projectId, issue, members, onInvited }) {
	const { people, toast } = useApp()
	const [selected, setSelected] = useState([])
	const [emails, setEmails] = useState("")
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState(null)

	const memberIds = new Set((members || []).map((m) => m.user_id))
	const candidates = people.filter((person) => !memberIds.has(person.user_id))

	useEffect(() => {
		if (open) return
		setSelected([])
		setEmails("")
		setError(null)
	}, [open])

	function toggle(list, setList, value) {
		setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value])
	}

	const parsedEmails = emails
		.split(/[\s,;]+/)
		.map((value) => value.trim())
		.filter(Boolean)

	const total = selected.length + parsedEmails.length

	async function submit() {
		setBusy(true)
		setError(null)
		try {
			const result = await api.addMembers(projectId, issue.issue_id, {
				user_ids: selected,
				emails: parsedEmails
			})
			const added = result.added?.length || 0
			const invited = result.invited_emails?.length || 0
			toast(
				[
					added ? `${added} added` : null,
					invited ? `${invited} invited by email` : null
				]
					.filter(Boolean)
					.join(", ") || "Nobody new to add",
				"success"
			)
			await onInvited()
			onClose()
		} catch (cause) {
			setError(cause.message)
		} finally {
			setBusy(false)
		}
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={`Invite to #${issue?.number}`}
			description="They will see this issue and nothing else in the project."
			footer={
				<>
					<Button onClick={onClose}>Cancel</Button>
					<Button
						variant="primary"
						icon={UserRoundPlus}
						onClick={submit}
						busy={busy}
						disabled={busy || total === 0}
					>
						{total ? `Invite ${total}` : "Invite"}
					</Button>
				</>
			}
		>
			<div className="space-y-4">
				{candidates.length > 0 && (
					<div>
						<p className="mb-1.5 text-[0.8125rem] font-medium text-ink-700">
							People you already work with
						</p>
						<div className="max-h-44 space-y-0.5 overflow-y-auto scrollbar-slim">
							{candidates.map((person) => {
								const on = selected.includes(person.user_id)
								return (
									<button
										key={person.user_id}
										type="button"
										onClick={() => toggle(selected, setSelected, person.user_id)}
										className={cx(
											"flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors",
											on ? "bg-brand-50" : "hover:bg-ink-100"
										)}
									>
										<Avatar person={person} size="sm" />
										<span className="min-w-0 flex-1">
											<span className="block truncate text-[0.8125rem] font-medium leading-tight text-ink-900">
												{person.name}
											</span>
											<span className="block truncate text-2xs leading-tight text-ink-500">
												{[person.role, person.company].filter(Boolean).join(", ") ||
													person.email}
											</span>
										</span>
										{on && (
											<span
												className="h-2 w-2 shrink-0 rounded-full bg-brand-600"
												aria-hidden
											/>
										)}
									</button>
								)
							})}
						</div>
					</div>
				)}

				<Field
					label="Or invite by email"
					hint="Anyone without an account gets a link to create one. Separate several with commas."
					error={error}
				>
					<Input
						value={emails}
						onChange={(event) => setEmails(event.target.value)}
						placeholder="name@company.com, other@firm.co.uk"
					/>
				</Field>

				{parsedEmails.length > 0 && (
					<div className="flex flex-wrap gap-1.5">
						{parsedEmails.map((address) => (
							<span
								key={address}
								className="inline-flex items-center gap-1.5 rounded-full bg-ink-100 px-2 py-0.5 text-2xs text-ink-700"
							>
								<Mail size={10} className="text-ink-400" aria-hidden />
								{address}
							</span>
						))}
					</div>
				)}

				<p className="flex items-start gap-2 rounded-lg bg-ink-50 px-3 py-2 text-2xs leading-relaxed text-ink-600">
					<UsersRound size={13} className="mt-0.5 shrink-0 text-ink-400" aria-hidden />
					<span>
						Need a whole group on everything, not just this one?{" "}
						<Link
							to={`/projects/${projectId}/team`}
							onClick={onClose}
							className="font-medium text-brand-700 hover:underline"
						>
							Put a team on the project
						</Link>{" "}
						instead — they will see every issue, including the ones raised later.
					</span>
				</p>
			</div>
		</Modal>
	)
}

/** The avatar strip plus a remove control, shown under the issue header. */
export function MemberStrip({ members, issue, onRemove }) {
	const { user } = useApp()
	if (!members?.length) return null

	return (
		<div className="flex flex-wrap items-center gap-1.5">
			<span className="text-2xs text-ink-500">On this issue:</span>
			{members.map((member) => (
				<span
					key={member.user_id}
					className="inline-flex items-center gap-1.5 rounded-full bg-ink-100 py-0.5 pl-0.5 pr-2 text-2xs text-ink-700"
					title={[member.name, member.role, member.company].filter(Boolean).join(" · ")}
				>
					<Avatar person={member} size="xs" />
					{member.user_id === user?.user_id ? "You" : member.name}
					{issue?.can_invite && member.user_id !== issue.created_by && (
						<button
							type="button"
							onClick={() => onRemove(member)}
							aria-label={`Remove ${member.name}`}
							className="-mr-1 rounded-full p-0.5 opacity-50 transition hover:bg-black/10 hover:opacity-100"
						>
							<X size={10} strokeWidth={3} aria-hidden />
						</button>
					)}
				</span>
			))}
		</div>
	)
}
